import type { IdCaptionType } from '../../@types/common';
import type {
  CompetenciesItem,
  CompetenciesResponse,
  CompetencyPatch,
  CompetencyTagsResult,
} from '../../@types/competencies';
import type {
  CompetenciesConfigurationItem,
  CompetenciesConfigurationResponse,
} from '../../@types/competencies-configuration';
import type {
  CompetencyGroupBaseApiProps,
  CompetencyGroupItem,
  CompetencyGroupsApiProps,
  CompetencyGroupsResponse,
} from '../../@types/competency-groups';
import { createFieldFilterText } from '../../utils/filter-url-builder-utils';
import { createCustomSortWithOrderBy } from '../../utils/sort-url-builder-utils';
import { providesList } from '../util/api-util';
import { api } from './api';

export const competencyApi = api.enhanceEndpoints({ addTagTypes: ['CompetencyGroup', 'Competency'] }).injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getCompetencyGroups: builder.query<Array<CompetencyGroupItem>, CompetencyGroupsApiProps>({
      query: (args) =>
        `/competency-groups?page=${args.page}&pageSize=${args.pageSize}${createCustomSortWithOrderBy(
          args.sortedFields
        )}${createFieldFilterText({
          filters: args.filters,
        })}`,
      transformResponse: (response: CompetencyGroupsResponse) => response?.items || [],
      providesTags: (result) => providesList(result, 'CompetencyGroup'),
    }),
    updateCompetencyGroup: builder.mutation<void, Pick<CompetencyGroupItem, 'id'> & Partial<CompetencyGroupItem>>({
      query: ({ id, ...patch }) => ({
        url: `/competency-groups/${id}`,
        method: 'PUT',
        body: patch,
        validateStatus: (response, result) => response.status === 200 && result?.result,
      }),

      invalidatesTags: (result, error, arg) => {
        if (result) {
          return [{ type: 'CompetencyGroup', id: arg.id }];
        } else {
          return [];
        }
      },
    }),
    getCompetencies: builder.query<Array<CompetenciesItem>, CompetencyGroupBaseApiProps>({
      query: (args) =>
        `/competencies/simple?page=${args.page}&pageSize=${args.pageSize}${createCustomSortWithOrderBy(
          args.sortedFields
        )}${createFieldFilterText({
          filters: args?.filters,
        })}`,
      transformResponse: (response: CompetenciesResponse) => response?.items || [],
      providesTags: (result) => providesList(result, 'Competency'),
    }),
    updateCompetency: builder.mutation<void, Pick<CompetencyGroupItem, 'id'> & Partial<CompetencyPatch>>({
      query: (patch) => ({
        url: `/competencies/${patch.id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, arg) => {
        if (result) {
          return [{ type: 'Competency', id: arg.id }];
        } else {
          return [];
        }
      },
    }),
    getCompetencyTags: builder.query<Array<IdCaptionType>, void>({
      query: () => `/competencies/tags`,
      transformResponse: (response: CompetencyTagsResult) => response?.result || [],
    }),
    getCompetenciesConfiguration: builder.query<CompetenciesConfigurationItem, void>({
      query: () => `/competencies/configuration`,
      transformResponse: (response: CompetenciesConfigurationResponse) => response?.result || {},
    }),
    updateCompetenciesConfiguration: builder.mutation<void, Partial<CompetenciesConfigurationItem>>({
      query: (patch) => ({
        url: `/competencies/configuration`,
        method: 'PATCH',
        body: patch,
      }),
    }),
  }),
});

export const {
  useGetCompetencyGroupsQuery,
  useLazyGetCompetencyGroupsQuery,
  useUpdateCompetencyGroupMutation,
  useGetCompetenciesQuery,
  useLazyGetCompetenciesQuery,
  useUpdateCompetencyMutation,
  useLazyGetCompetenciesConfigurationQuery,
  useUpdateCompetenciesConfigurationMutation,
} = competencyApi;
